import { ButtonWithDialog } from "@focus/focusUI";
import { useMemo, useState } from "react";
import { ROLES, useRoles } from "../../hooks/useRoles";
import { useUserController } from "../../hooks/useUserController";
import type { User } from "../../types/users";
import { UserForm, type ExtendedUser } from "./UserForm";

export const EditUserButton = ({
  user,
  disabled,
  isSelf,
}: {
  user?: User;
  disabled?: boolean;
  isSelf?: boolean;
}) => {
  const { organizations, updateUser } = useUserController();
  const { roles } = useRoles();
  const [acceptDisabled, setAcceptDisabled] = useState<boolean>(true);

  const canEdit = useMemo(() => {
    if (roles.includes(ROLES.ADMIN)) return true;
    if (!roles.includes(ROLES.ORG_ADMIN)) return false;

    return (
      user?.organizations?.some((org) => organizations.includes(org)) ?? false
    );
  }, [roles, organizations, user?.organizations]);

  return (
    <ButtonWithDialog<User | ExtendedUser>
      icon={canEdit ? "edit" : "eye-open"}
      minimal
      intent="none"
      defaultContent={user}
      onAccept={(updated) => {
        if (canEdit) updateUser(updated);
      }}
      disableAccept={!canEdit || acceptDisabled}
      form={({ content, onUpdate }) => (
        <UserForm
          content={content}
          onUpdate={onUpdate}
          onValidityChange={(isValid) => setAcceptDisabled(!isValid)}
          readOnly={!canEdit}
          isSelf={isSelf}
        />
      )}
      hideButtons={!canEdit}
      headerTitle={canEdit ? "Edit User" : "User Info"}
      tooltipText={canEdit ? "Edit user" : "User Info"}
      disabled={disabled}
    />
  );
};
